import { FC, useState, useCallback } from 'react'
import { useTranslate } from 'react-admin'
import { Autocomplete, CircularProgress, TextField as MuiTextField } from '@mui/material'
import { getJson, getUrl } from '../providers/data'
import { Auth0UserOut } from '../types/apiRequests'

interface Auth0UserAutocompleteProps {
  auth0OrgId?: string | null
  onSelect: (user: Auth0UserOut | null) => void
  label?: string
  freeSolo?: boolean
  onInputChange?: (value: string) => void
  initialValue?: string
  disabled?: boolean
  size?: 'small' | 'medium'
}

const Auth0UserAutocomplete: FC<Auth0UserAutocompleteProps> = ({
  auth0OrgId,
  onSelect,
  label,
  freeSolo = false,
  onInputChange: onInputChangeProp,
  initialValue = '',
  disabled = false,
  size,
}) => {
  const translate = useTranslate()
  const [options, setOptions] = useState<Auth0UserOut[]>([])
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState(initialValue)

  const handleSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setOptions([])
      return
    }
    setLoading(true)
    try {
      const orgParam = auth0OrgId ? `&org_id=${encodeURIComponent(auth0OrgId)}` : ''
      const results = await getJson<Auth0UserOut[]>(
        `${getUrl()}/auth0_user?q=${encodeURIComponent(query)}${orgParam}`
      )
      setOptions(results)
    } catch {
      setOptions([])
    } finally {
      setLoading(false)
    }
  }, [auth0OrgId])

  return (
    <Autocomplete
      options={options}
      freeSolo={freeSolo}
      disabled={disabled}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.email || option.name || option.user_id
      }
      renderOption={(props, option) => (
        <li {...props} key={typeof option === 'string' ? option : option.user_id}>
          {typeof option === 'string' ? option : (
            <div>
              <div style={{ fontWeight: 500 }}>{option.name || option.email}</div>
              {option.email && option.name && (
                <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>{option.email}</div>
              )}
            </div>
          )}
        </li>
      )}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        setInputValue(value)
        if (reason === 'input') {
          void handleSearch(value)
          onInputChangeProp?.(value)
        }
      }}
      onChange={(_, value) => {
        if (typeof value === 'string') {
          onInputChangeProp?.(value)
        } else {
          onSelect(value)
        }
      }}
      loading={loading}
      noOptionsText={inputValue.length < 2 ? translate('data.typeToSearch') : translate('data.noResults')}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label={label || translate('data.searchAuth0User')}
          fullWidth
          margin="dense"
          size={size}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{ mb: 1 }}
    />
  )
}

export default Auth0UserAutocomplete
